import { Injectable } from '@angular/core';

import { first, map, Observable, switchMap } from 'rxjs';

import { GoogleTagManagerService } from 'angular-google-tag-manager';

import { ActorQuery } from './actor.query';
import { ActorStore } from './actor.store';
import { ActorLoaderKey } from '../../enums';
import { Actor, IUser, MeResponse, Organisation, UserinfoResponse } from '../../models';
import { ApiService } from '../api.service';
import { LoadingService } from '../loading/loading.service';
import { SnackbarService } from '../snackbar.service';

const USERINFO_ENDPOINT = 'auth/userinfo';
const ME_ENDPOINT = 'v1/auth/me';

@Injectable({ providedIn: 'root' })
export class ActorService {
  constructor(
    private api: ApiService,
    private query: ActorQuery,
    private store: ActorStore,
    private loader: LoadingService,
    private snack: SnackbarService,
    private gtm: GoogleTagManagerService,
  ) {}

  getUserInfo(): Observable<void> {
    return this.api.get<UserinfoResponse>(USERINFO_ENDPOINT).pipe(
      first(),
      switchMap((userinfo: UserinfoResponse) => {
        this.resolveCid(userinfo.profile.organisations);
        return this.api.get<MeResponse>(ME_ENDPOINT).pipe(
          first(),
          map((me: MeResponse) => this.commit(userinfo, me)),
        );
      }),
    );
  }

  get(): Actor {
    return this.query.getValue();
  }

  select(): Observable<Actor> {
    return this.query.select();
  }

  update(name: string, initials: string, bgColor: string): Observable<void> {
    return this.api
      .patch<void, { name: string; initials: string; bgColor: string }>(USERINFO_ENDPOINT, { name, initials, bgColor })
      .pipe(
        first(),
        map((): void => {
          const profile = this.get().profile;

          this.store.update({ profile: { ...profile, name, avatar: { initials, bgColor } } } as Actor);
        }),
        this.loader.set(ActorLoaderKey.USER_SAVE),
        this.snack.operator('Profile updated'),
      );
  }

  private resolveCid(organisations: Organisation[]): void {
    const stored: string | null = sessionStorage.getItem('cid');
    const isStoredValid: boolean = !!stored && organisations.some((org: Organisation) => org.id === stored);
    const cid: string | undefined = isStoredValid ? stored ?? undefined : organisations[0]?.id;

    if (!cid) {
      sessionStorage.removeItem('cid');
      return;
    }

    sessionStorage.setItem('cid', cid);
  }

  private commit(userinfo: UserinfoResponse, me: MeResponse): void {
    const profile: IUser = {
      ...userinfo.profile,
      groups: me.groups,
    };
    const actor: Actor = {
      id: userinfo.id,
      token: '',
      intercom: userinfo.intercom,
      profile,
      partnerId: me.partnerId,
      currentOrg: me.currentOrg,
      permissions: me.permissions,
      isWarehouseContact: me.isWarehouseContact,
    };

    this.store.update(actor);

    void this.gtm.pushTag({
      event: 'profile',
      id: actor.id,
      name: actor.profile.name,
      email: actor.profile.email,
      user_hash: actor.intercom,
      hub_workarea: actor.currentOrg?.name,
    });
  }
}
